import { PluginListenerHandle } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { isPlatform } from '@ionic/react';
import { useEffect } from 'react';
import {
  UserDeviceType,
  useRegisterUserDeviceMutation,
} from '../../api/graphql/generated';
import { useNavigateTo } from '../../pages/redirect.hooks';
import { logger } from '../../util/logger';

interface PushNotificationData {
  url?: string;
  deeplink?: string;
}

const PushNotificationListener = (): null => {
  const [registerUserDevice] = useRegisterUserDeviceMutation();
  const [navigateTo] = useNavigateTo();

  useEffect(() => {
    if (!isPlatform('capacitor')) {
      return;
    }

    let registrationListener: PluginListenerHandle;
    let registrationErrorListener: PluginListenerHandle;
    let pushNotificationReceivedListener: PluginListenerHandle;
    let pushNotificationActionPerformedListener: PluginListenerHandle;

    const registerListeners = async () => {
      const handlePushNotification = (
        notification: PushNotificationSchema
      ): void => {
        // we have observed leaks in the push notification listeners on mobile when using window.location.href
        // because this component never gets the unmount executed. since this is meant to be a single instance
        // we will forcefull remove all previous listeners here to prevent leaking listeners and responding
        // to push notifications multiple times
        PushNotifications.removeAllListeners();

        const getDataFromNotification = (
          notification: PushNotificationSchema
        ): PushNotificationData | undefined => {
          if (isPlatform('ios')) {
            if (notification?.data?.data?.pinpoint) {
              return notification.data.data.pinpoint;
            } else if (notification?.data?.aps) {
              return notification.data.aps;
            }
          } else {
            return notification.data;
          }
        };

        const data = getDataFromNotification(notification);

        if (data) {
          const url = data.url || data.deeplink;

          if (url) {
            navigateTo({
              url,
            });
          }
        }
      };

      registrationListener = await PushNotifications.addListener(
        'registration',
        async (token: Token) => {
          const input = {
            token: token.value,
            type: isPlatform('android')
              ? UserDeviceType.ANDROID
              : UserDeviceType.IOS,
          };

          await registerUserDevice({
            variables: {
              input,
            },
          });
        }
      );

      registrationErrorListener = await PushNotifications.addListener(
        'registrationError',
        (errorVal) => {
          logger.error('error registering push notification', { errorVal });
        }
      );

      pushNotificationReceivedListener = await PushNotifications.addListener(
        'pushNotificationReceived',
        async (_notification: PushNotificationSchema) => {
          // If we want to take action on received notifications
        }
      );

      pushNotificationActionPerformedListener =
        await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          async (action: ActionPerformed) => {
            handlePushNotification(action.notification);
          }
        );
    };

    registerListeners();

    return () => {
      registrationListener?.remove();
      registrationErrorListener?.remove();
      pushNotificationReceivedListener?.remove();
      pushNotificationActionPerformedListener?.remove();
    };
  }, [navigateTo, registerUserDevice]);

  return null;
};

export { PushNotificationListener };
